import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, UserCog, Users2, Pencil, Trash2, HelpCircle, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { adminAPI } from '../services/adminApi';
import { useAuth } from '../context/AdminAuthContext';
import './Users.css';

const PAGE_SIZE = 10;

const TABS = [
  { key: 'superadmin', label: 'Super Admins', Icon: ShieldCheck, addLabel: '+ Add SA', searchPlaceholder: 'Search superadmins…' },
  { key: 'admin', label: 'Admins', Icon: UserCog, addLabel: '+ Add Admin', searchPlaceholder: 'Search admins…' },
  { key: 'user', label: 'Users', Icon: Users2, addLabel: '+ Add User', searchPlaceholder: 'Search users…' },
];

const EMPTY_ADD_FORM = { first_name: '', last_name: '', email: '', password: '' };
const EMPTY_EDIT_FORM = { first_name: '', last_name: '', email: '' };

const Users = () => {
  const { isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState(isSuperAdmin ? 'superadmin' : 'user');
  const visibleTabs = isSuperAdmin ? TABS : TABS.filter(t => t.key === 'user');

  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_ADD_FORM);
  const [addError, setAddError] = useState('');
  const [adding, setAdding] = useState(false);

  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM);
  const [editError, setEditError] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [confirmAction, setConfirmAction] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const tab = TABS.find(t => t.key === activeTab);

  const load = useCallback(() => {
    setLoading(true);
    adminAPI.getUsers({ role: activeTab, search: search || undefined })
      .then(r => setAllUsers(r.data || []))
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [activeTab, search]);

  useEffect(() => { setPage(1); }, [activeTab, search]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(allUsers.length / PAGE_SIZE));
  const pageUsers = allUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openAdd = () => { setAddForm(EMPTY_ADD_FORM); setAddError(''); setShowAdd(true); };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddError('');
    setAdding(true);
    try {
      const { data } = await adminAPI.createUser({ ...addForm, role: activeTab });
      setAllUsers(list => [data.user, ...list]);
      setShowAdd(false);
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to add');
    } finally {
      setAdding(false);
    }
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setEditForm({ first_name: u.first_name || '', last_name: u.last_name || '', email: u.email || '' });
    setEditError('');
    setChangePassword(false);
    setNewPassword('');
    setShowNewPassword(false);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditError('');
    if (changePassword && newPassword.length < 3) {
      setEditError('New password must be at least 3 characters');
      return;
    }
    setSavingEdit(true);
    try {
      const payload = { ...editForm };
      if (changePassword && newPassword) payload.password = newPassword;
      const { data } = await adminAPI.updateUser(editingUser.id, payload);
      setAllUsers(list => list.map(u => u.id === editingUser.id ? { ...u, ...data } : u));
      setEditingUser(null);
      alert(changePassword ? 'User updated and password changed successfully.' : 'User updated successfully.');
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update');
    } finally {
      setSavingEdit(false);
    }
  };

  const runConfirmed = async () => {
    if (!confirmAction) return;
    setConfirming(true);
    const { type, user } = confirmAction;
    try {
      if (type === 'status') {
        await adminAPI.updateUser(user.id, { is_active: !user.is_active });
        setAllUsers(list => list.map(u => u.id === user.id ? { ...u, is_active: !user.is_active } : u));
      } else {
        await adminAPI.deleteUser(user.id);
        setAllUsers(list => list.filter(u => u.id !== user.id));
      }
      setConfirmAction(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Action failed');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="admin-users-page">
      {visibleTabs.length > 1 && (
        <div className="admin-role-tabs">
          {visibleTabs.map(t => (
            <button
              key={t.key}
              className={`admin-role-tab ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              <t.Icon size={16} /> {t.label.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      <div className="admin-users-toolbar">
        <input
          className="admin-search-input"
          placeholder={tab.searchPlaceholder}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {(isSuperAdmin || activeTab === 'user') && (
          <button className="admin-btn-primary" onClick={openAdd}>{tab.addLabel}</button>
        )}
      </div>

      <div className="admin-table-card">
        <table className="admin-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Status</th><th>Action</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="admin-table-empty">Loading…</td></tr>
            ) : pageUsers.length === 0 ? (
              <tr><td colSpan={4} className="admin-table-empty">No {tab.label.toLowerCase()} found.</td></tr>
            ) : (
              pageUsers.map(u => (
                <tr key={u.id}>
                  <td>{u.first_name} {u.last_name}</td>
                  <td>
                    {u.email}
                    {activeTab === 'user' && <div className="admin-hardcoded-pw-note">Password: 123456</div>}
                  </td>
                  <td>
                    <button
                      className={`admin-status-badge clickable ${u.is_active ? 'active' : 'inactive'}`}
                      onClick={() => setConfirmAction({ type: 'status', user: u })}
                    >
                      {u.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="admin-table-actions">
                    <div className="admin-table-actions-inner">
                      <button
                        className="admin-icon-btn"
                        title="Edit"
                        onClick={() => openEdit(u)}
                      >
                        <Pencil size={14} />
                      </button>

                      {(isSuperAdmin || activeTab === 'user') && (
                        <button
                          className="admin-icon-btn danger"
                          title="Delete"
                          onClick={() =>
                            setConfirmAction({ type: 'delete', user: u })
                          }
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="pagination">
          <span className="pg-info">
            Showing {allUsers.length > 0 ? (page - 1) * PAGE_SIZE + 1 : 0} to {Math.min(page * PAGE_SIZE, allUsers.length)} of {allUsers.length} entries
          </span>
          <div className="pg-controls">
            <button className="pg-btn pg-btn-word" onClick={() => setPage(p => p - 1)} disabled={page === 1}>Previous</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p} className={`pg-btn ${page === p ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
            ))}
            <button className="pg-btn pg-btn-word" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>Next</button>
          </div>
        </div>
      </div>

      {/* Add modal */}
      {showAdd && (
        <div className="admin-modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{tab.addLabel.replace('+ ', '')}</h2>
              <button className="admin-modal-close" onClick={() => setShowAdd(false)}>✕</button>
            </div>
            <form onSubmit={handleAdd} className="admin-modal-body">
              {addError && <div className="admin-auth-error">{addError}</div>}
              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>First Name</label>
                  <input placeholder="Enter First Name" value={addForm.first_name} onChange={e => setAddForm(f => ({ ...f, first_name: e.target.value }))} required />
                </div>
                <div className="admin-form-group">
                  <label>Last Name</label>
                  <input placeholder="Enter Last Name" value={addForm.last_name} onChange={e => setAddForm(f => ({ ...f, last_name: e.target.value }))} required />
                </div>
              </div>
              <div className="admin-form-group">
                <label>Email</label>
                <input type="email" placeholder="Enter Email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} required />
              </div>
              <div className="admin-form-group">
                <label>Password</label>
                <input type="text" placeholder="Enter Password" value={addForm.password} onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))} required minLength={3} />
              </div>
              <div className="admin-modal-actions">
                <button type="button" className="admin-btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={adding}>{adding ? 'Adding…' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editingUser && (
        <div className="admin-modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Edit {tab.label.slice(0, -1)}</h2>
              <button className="admin-modal-close" onClick={() => setEditingUser(null)}>✕</button>
            </div>
            <form onSubmit={handleEdit} className="admin-modal-body">
              {editError && <div className="admin-auth-error">{editError}</div>}
              <div className="admin-form-group">
                <label>First Name</label>
                <input value={editForm.first_name} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))} required />
              </div>
              <div className="admin-form-group">
                <label>Last Name</label>
                <input value={editForm.last_name} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))} required />
              </div>
              <div className="admin-form-group">
                <label>Email</label>
                <input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} required />
              </div>

              <div className="admin-form-group admin-change-pw-toggle">
                <label className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    checked={changePassword}
                    onChange={e => { setChangePassword(e.target.checked); setNewPassword(''); }}
                  />
                  Change Password
                </label>
              </div>

              {changePassword && (
                <div className="admin-form-group">
                  <label>New Password</label>
                  <div className="admin-password-field">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      minLength={3}
                      required
                    />
                    <button
                      type="button"
                      className="admin-password-toggle"
                      onClick={() => setShowNewPassword(s => !s)}
                      tabIndex={-1}
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                    >
                      {showNewPassword ? <Eye size={17} /> : <EyeOff size={17} />}
                    </button>
                  </div>
                </div>
              )}

              <div className="admin-modal-actions">
                <button type="button" className="admin-btn-secondary" onClick={() => setEditingUser(null)}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={savingEdit}>{savingEdit ? 'Updating…' : 'Update'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Status / delete confirmation */}
      {confirmAction && (
        <div className="admin-modal-overlay" onClick={() => setConfirmAction(null)}>
          <div className="admin-confirm-modal" onClick={e => e.stopPropagation()}>
            {confirmAction.type === 'status' ? (
              <>
                <HelpCircle size={44} className="admin-confirm-icon info" />
                <h3>Change status to {confirmAction.user.is_active ? 'Inactive' : 'Active'}?</h3>
              </>
            ) : (
              <>
                <AlertTriangle size={44} className="admin-confirm-icon warn" />
                <h3>Do you want to delete it?</h3>
              </>
            )}
            <div className="admin-confirm-actions">
              <button className="admin-btn-primary" onClick={runConfirmed} disabled={confirming}>
                {confirming ? 'Please wait…' : confirmAction.type === 'delete' ? 'Yes, delete it' : 'Yes, change it'}
              </button>
              <button className="admin-btn-secondary" onClick={() => setConfirmAction(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;